import { motion } from 'framer-motion';
import {
  Mail, Phone, Clock, ShieldCheck, Lock, Truck, RotateCcw, Award,
} from 'lucide-react';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from './ui/accordion';
import { InlineConcierge } from './InlineConcierge';
import { CONTACT_FAQS, COMPANY } from '../data/legalContent';

const WaIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const TRUST_BADGES = [
  { icon: Award, text: 'USAPA Approved' },
  { icon: Lock, text: 'Secure Razorpay' },
  { icon: Truck, text: 'Pan-India Shipping' },
  { icon: RotateCcw, text: 'Defect Returns' },
  { icon: ShieldCheck, text: '6-Month Warranty' },
];

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const REP_IMG = `${process.env.REACT_APP_BACKEND_URL}/api/static/products/care_rep.png`;

const ContactCard = ({ icon: Icon, title, subtitle, href, testid }) => {
  const inner = (
    <>
      <Icon className="w-6 h-6 text-zinc-900 mb-3 group-hover:text-[#E02128] transition-colors" />
      <p className="text-sm font-semibold text-[#111214]">{title}</p>
      <p className="text-xs text-[#52525B] mt-1 break-words">{subtitle}</p>
    </>
  );
  const cls =
    'block bg-white border border-zinc-200 rounded-xl p-5 hover:-translate-y-1 hover:shadow-md transition-all duration-300 group h-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E02128] focus-visible:ring-offset-2';
  return href ? (
    <a href={href} target={href.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer" className={cls} data-testid={testid}>
      {inner}
    </a>
  ) : (
    <div className={cls} data-testid={testid}>{inner}</div>
  );
};

export const ContactSection = () => {
  return (
    <section className="py-12 md:py-16 bg-[#F9F9FA]" id="contact">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Heading — care specialist cutout to the LEFT of the title */}
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-80px' }}
          variants={fadeUp}
          className="mb-8 md:mb-10 flex items-end gap-4 sm:gap-7 md:gap-9"
        >
          <img
            src={REP_IMG}
            alt="DYSSC care specialist"
            data-testid="care-rep-image"
            className="h-28 sm:h-32 md:h-40 lg:h-44 w-auto object-contain object-bottom select-none pointer-events-none shrink-0 self-end -scale-x-100"
          />
          <div className="max-w-2xl pb-1 sm:pb-2">
            <p className="uppercase text-xs tracking-widest font-semibold text-[#E02128] mb-3">We're here to help</p>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight text-[#111214]">Get in touch</h2>
            <p className="text-[#52525B] leading-relaxed mt-4 text-base">
              Questions about our paddles, your order, or the tech inside? Ask our support team for an instant answer, or reach us any way you like.
            </p>
          </div>
        </motion.div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-stretch">
          {/* Left: contact method cards */}
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: '-60px' }}
            variants={{ hidden: {}, show: { transition: { staggerChildren: 0.08 } } }}
            className="lg:col-span-5"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <motion.div variants={fadeUp}>
                <ContactCard
                  icon={WaIcon}
                  title="WhatsApp"
                  subtitle="Chat with our team instantly"
                  href={`https://wa.me/${COMPANY.whatsapp}`}
                  testid="contact-card-whatsapp"
                />
              </motion.div>
              <motion.div variants={fadeUp}>
                <ContactCard
                  icon={Mail}
                  title="Email"
                  subtitle={COMPANY.email}
                  href={`mailto:${COMPANY.email}`}
                  testid="contact-card-email"
                />
              </motion.div>
              <motion.div variants={fadeUp}>
                <ContactCard
                  icon={Phone}
                  title="Call us"
                  subtitle={COMPANY.phone}
                  href={`tel:${COMPANY.phone.replace(/\s/g, '')}`}
                  testid="contact-card-phone"
                />
              </motion.div>
              <motion.div variants={fadeUp}>
                <ContactCard
                  icon={Clock}
                  title="Support Hours"
                  subtitle="Mon–Fri · 9 AM – 6 PM IST"
                  testid="contact-card-hours"
                />
              </motion.div>
            </div>

          </motion.div>

          {/* Right: inline concierge */}
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: '-60px' }}
            variants={fadeUp}
            className="lg:col-span-7"
          >
            <InlineConcierge />
          </motion.div>
        </div>

        {/* Trust badges */}
        <div className="flex flex-wrap justify-center lg:justify-start gap-x-8 gap-y-4 py-6 border-t border-zinc-200 mt-10">
          {TRUST_BADGES.map((b) => (
            <div key={b.text} className="flex items-center gap-2" data-testid={`trust-badge-${b.text.toLowerCase().split(' ')[0]}`}>
              <b.icon className="w-5 h-5 text-zinc-400" />
              <span className="text-sm font-medium text-zinc-600">{b.text}</span>
            </div>
          ))}
        </div>

        {/* FAQ */}
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-4">
            <p className="uppercase text-xs tracking-widest font-semibold text-[#E02128] mb-3">FAQ</p>
            <h3 className="font-display text-3xl tracking-tight text-[#111214]">Quick answers</h3>
            <p className="text-[#52525B] leading-relaxed mt-3 text-sm">
              Can't find what you need? Ask DYSSC support above or message us on WhatsApp.
            </p>
          </div>
          <div className="lg:col-span-8">
            <Accordion type="single" collapsible className="w-full" data-testid="contact-faq">
              {CONTACT_FAQS.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-b border-zinc-200">
                  <AccordionTrigger
                    className="text-left text-[#111214] font-heading font-semibold hover:no-underline py-4"
                    data-testid={`faq-trigger-${i}`}
                  >
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[#52525B] leading-relaxed text-[15px] pb-4">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </div>
    </section>
  );
};
